import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { CallStatus } from '../../enums';
import { PagedResponse, SimplePagedResponse } from '../../models/common-response-types';
import { Company } from '../../models/company';
import {
  AvailableIndexesResponse,
  BalanceSheet,
  BaseRatiosParamType,
  BaseStatementSearchParamType,
  CashFlow,
  CompanyLogoAndNameResponse,
  DividendPayerData,
  Earning,
  EpicMoversParamType,
  EpicMoversResponse,
  ETF,
  ETFDetailParams,
  ETFDetailResponse,
  ETFParams,
  GapperParamType,
  GapperResponse,
  HighGrowthCompanyData,
  Income,
  LiquidityAndSolvencyRatiosResponse,
  NewStockData,
  NonWorkingDaysResponse,
  PageableFilterRequest,
  PopularStock,
  PopularStockParams,
  ProfitabilityRatiosResponse,
  RatiosResponse,
  Sector,
  SectorDetail,
  StatementApiResponse,
  StocksSymbolSet,
  ValuationRatiosResponse,
  Week52HighLowParamType,
  Week52HighLowResponse,
  Week52HighLowStatusType,
  Week52HighLowTopListResponse,
  MarketCapsParamType,
  MarketCapsSplitData,
} from '../../models/fundamental/types';
import { DEFAULT_PAGE_RESPONSE } from '../../models/pageable';
import { defaultLogoBase64 } from '../../util/assets/logos';
import { uniqBy } from '../../util/collectionHelpers';
import { NullableNumber } from '../../util/types';
import { ErrorPayloadFull } from '../common-actions';

import {
  DEFAULT_EPIC_MOVERS_DATA,
  DEFAULT_RATIOS_AND_FILTER_PARAMS,
  INITIAL_STATE,
  marketIndexesArray,
  NAMES_SECTORS_CONTAINING_IMAGES,
  NO_MORE_PAGES_TO_READ,
  DEFAULT_MARKET_CAPS_DATA,
  DEFAULT_PAGE,
} from './constants';
import {
  getPreviousEarningsCacheKey,
  parseETFName,
  populateEarningCalendarLabels,
  processCallCache,
  setFundamentalCallStatus,
  updateCompanyAndLogoCacheWithName,
  mergeMarketCapsPage,
} from './helpers';
import {
  EarningsCalendarSearchState,
  FundamentalCall,
  FundamentalState,
  GetPreviousEarningsPayload,
  GetSectorsResponse,
  SearchEarningsPayload,
  SearchStockPayload,
} from './types';

const fundamentalSlice = createSlice({
  name: 'fundamental',
  initialState: INITIAL_STATE,
  reducers: {
    searchStockByParams(state, action: PayloadAction<SearchStockPayload>) {
      const { stockQuery } = state;
      const { query } = action.payload;
      state.stockQuery = query;

      if (!query || query !== stockQuery) {
        state.stockResults = [];
      }

      setFundamentalCallStatus(FundamentalCall.searchStocks, !query ? CallStatus.READY : CallStatus.PENDING, state);
    },
    searchStock(state, action: PayloadAction<string>) {
      state.stockQuery = action.payload;
      setFundamentalCallStatus(FundamentalCall.searchStocks, CallStatus.PENDING, state);
    },
    stockSearchCompleted(state, action) {
      state.stockResults = action.payload;
      setFundamentalCallStatus(FundamentalCall.searchStocks, CallStatus.READY, state);
    },
    stockSearchFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.searchStocks, CallStatus.ERROR, state, status);
    },
    clearStockSearch(state) {
      state.stockQuery = '';
      state.stockResults = [];
      setFundamentalCallStatus(FundamentalCall.searchStocks, CallStatus.INITIAL, state);
    },
    stockSearchReset(state, action: PayloadAction<boolean>) {
      state.stockResults = [];
      if (action.payload) {
        setFundamentalCallStatus(FundamentalCall.searchStocks, CallStatus.INITIAL, state);
      }
    },
    loadStock(state, action: PayloadAction<{ symbol: string, size?: NullableNumber }>) {
      state.symbol = action.payload.symbol;
    },
    searchEarnings(state, action: PayloadAction<SearchEarningsPayload>) {
      setFundamentalCallStatus(FundamentalCall.searchEarnings, CallStatus.PENDING, state);
    },
    searchEarningsCompleted(state, action: PayloadAction<PagedResponse<Earning>>) {
      const { number: page, content } = populateEarningCalendarLabels(action.payload) ?? {};
      state.earningsSearchResponse = page === 0 ? populateEarningCalendarLabels(action.payload) : {
        ...populateEarningCalendarLabels(action.payload),
        content: state.earningsSearchResponse.content.concat(content),
      };
      setFundamentalCallStatus(FundamentalCall.searchEarnings, CallStatus.READY, state);
    },
    searchEarningsFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.searchEarnings, CallStatus.ERROR, state, status);
    },
    setEarningsCalendarSearchState(state, action: PayloadAction<Partial<EarningsCalendarSearchState>>) {
      state.earningsCalendarSearchState = {
        ...state.earningsCalendarSearchState,
        ...action.payload,
      };
    },
    getPreviousEarnings(state, action: PayloadAction<GetPreviousEarningsPayload>) {
      if (processCallCache(getPreviousEarningsCacheKey(action.payload), 'getPreviousEarnings') !== '') {
        setFundamentalCallStatus(FundamentalCall.getPreviousEarnings, CallStatus.PENDING, state);
      }
    },
    getPreviousEarningsCompleted(state, action: PayloadAction<{ key: string, content: Earning[] }>) {
      state.previousEarnings[action.payload.key] = action.payload.content;
      setFundamentalCallStatus(FundamentalCall.getPreviousEarnings, CallStatus.READY, state);
    },
    getPreviousEarningsFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getPreviousEarnings, CallStatus.ERROR, state, status);
    },
    keyStatsFailed(state, action) {
      state.keyStats = [];
      state.error = action.payload;
    },
    keyStatsCompleted(state, action) {
      state.keyStats = action.payload;
    },
    getCompany(state, action: PayloadAction<string>) {},
    getCompanyCompleted(state: FundamentalState, action: PayloadAction<Company[]>) {
      state.companies = [ ...state.companies, ...action.payload ];
    },
    getCompanyFailed(state, action) {
      state.companies = [];
      state.error = action.payload;
    },
    getCompanyLogoAndName(state, action: PayloadAction<string>) {
      if (processCallCache(action.payload, 'getCompanyLogoAndName') !== '') {
        setFundamentalCallStatus(FundamentalCall.getCompanyLogoAndName, CallStatus.PENDING, state);
      }
    },
    getCompanyLogoAndNameCompleted(state, action: PayloadAction<CompanyLogoAndNameResponse[]>) {
      action.payload?.forEach(item => {
        state.companyLogoAndName[item.symbol] = {
          url: item.url,
          logo: item.url ?? defaultLogoBase64,
          name: item.name,
          stockExchangeCode: item.stockExchangeCode,
          countryDesignation: item.countryDesignation,
        };
      });
      setFundamentalCallStatus(FundamentalCall.getCompanyLogoAndName, CallStatus.READY, state);
    },
    getCompanyLogoAndNameFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getCompanyLogoAndName, CallStatus.ERROR, state, status);
    },
    getPopularStocks(state, action: PayloadAction<PopularStockParams | undefined>) {
      setFundamentalCallStatus(FundamentalCall.getPopularStocks, CallStatus.PENDING, state);
    },
    getPopularStocksCompleted(state, action: PayloadAction<PopularStock[]>) {
      const stocks = action.payload;
      state.popularStocks = stocks.map(({
        symbol,
        popularity,
        symbolUniverseSymbol,
        stockExchangeCode,
      }) => ({ symbol, popularity, symbolUniverseSymbol, stockExchangeCode }));
      setFundamentalCallStatus(FundamentalCall.getPopularStocks, CallStatus.READY, state);
      updateCompanyAndLogoCacheWithName(stocks, state);
    },
    getPopularStocksFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getPopularStocks, CallStatus.ERROR, state, status);
    },
    getNewStocks(state, action: PayloadAction<StocksSymbolSet | undefined>) {
      setFundamentalCallStatus(FundamentalCall.getNewStocks, CallStatus.PENDING, state);
    },
    getNewStocksCompleted(state, action: PayloadAction<NewStockData[]>) {
      const stocks = action.payload;
      state.newStocks = stocks;
      setFundamentalCallStatus(FundamentalCall.getNewStocks, CallStatus.READY, state);
      updateCompanyAndLogoCacheWithName(stocks, state);
    },
    getNewStocksFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getNewStocks, CallStatus.ERROR, state, status);
    },
    getDividendPayers(state, action: PayloadAction<PageableFilterRequest|undefined>) {
      setFundamentalCallStatus(FundamentalCall.getDividendPayers, CallStatus.PENDING, state);
    },
    getDividendPayersCompleted(state, action: PayloadAction<PagedResponse<DividendPayerData>>) {
      const { content, number } = action.payload;
      const uniqueContent = uniqBy(content, 'symbol');
      state.dividendPayers = number === 0 ? { ...action.payload, content: uniqueContent } : {
        ...action.payload,
        content: state.dividendPayers.content.concat(uniqueContent),
      };
      setFundamentalCallStatus(FundamentalCall.getDividendPayers, CallStatus.READY, state);
      updateCompanyAndLogoCacheWithName(content, state);
    },
    getDividendPayersFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getDividendPayers, CallStatus.ERROR, state, status);
    },
    getHighGrowthCompanies(state, action: PayloadAction<PageableFilterRequest|undefined>) {
      setFundamentalCallStatus(FundamentalCall.getHighGrowthCompanies, CallStatus.PENDING, state);
    },
    getHighGrowthCompaniesCompleted(state, action: PayloadAction<PagedResponse<HighGrowthCompanyData>>) {
      const { content, number } = action.payload;
      const uniqueContent = uniqBy(content, 'symbol');
      state.highGrowthCompanies = number === 0 ? { ...action.payload, content: uniqueContent } : {
        ...action.payload,
        content: state.highGrowthCompanies.content.concat(uniqueContent),
      };
      setFundamentalCallStatus(FundamentalCall.getHighGrowthCompanies, CallStatus.READY, state);
      updateCompanyAndLogoCacheWithName(content, state);
    },
    getHighGrowthCompaniesFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getHighGrowthCompanies, CallStatus.ERROR, state, status);
    },
    getSectors(state, action: PayloadAction<undefined>) {
      setFundamentalCallStatus(FundamentalCall.getSectors, CallStatus.PENDING, state);
    },
    getSectorsCompleted(state, action: PayloadAction<GetSectorsResponse[]>) {
      const tempSectors: Sector[] = [];
      action.payload
        .filter(sector => sector.sector !== 'N/A' && sector.sector !== 'NULL')
        .filter(sector => NAMES_SECTORS_CONTAINING_IMAGES.includes(sector.sector))
        .forEach(sec => {
          const {
            id,
            sector,
            symbolUniverseSymbolCount: symbolCount,
            url,
          } = sec;

          tempSectors.push({ id, sector, symbolCount: symbolCount || 0, url });
        });

      state.sectors = tempSectors;
      setFundamentalCallStatus(FundamentalCall.getSectors, CallStatus.READY, state);
    },
    getSectorsFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getSectors, CallStatus.ERROR, state, status);
    },
    getSectorDetail(state, action: PayloadAction<number>) {
      setFundamentalCallStatus(FundamentalCall.getSectorDetails, CallStatus.PENDING, state);
    },
    getSectorDetailCompleted(state, action: PayloadAction<SectorDetail>) {
      const response = {
        ...action.payload,
        // This is for adding default logo if no logo is available
        content: action.payload.content?.map(el => (el?.logoBase64 ? el : { ...el, logoBase64: defaultLogoBase64 })),
      };

      if (state.sectorDetails.sectorId === response.sectorId) {
        response.content.map(item => state.sectorDetails.content.push(item));
      } else {
        state.sectorDetails = { ...state.sectorDetails, content: response.content, nextPageToRead: 0 };
      }

      state.sectorDetails.nextPageToRead = state.sectorDetails.content?.length >= response.totalElements!
        ? NO_MORE_PAGES_TO_READ
        : state.sectorDetails.nextPageToRead += 1;
      state.sectorDetails.sector = response.sector;
      state.sectorDetails.sectorId = response.sectorId;
      setFundamentalCallStatus(FundamentalCall.getSectorDetails, CallStatus.READY, state);
      updateCompanyAndLogoCacheWithName(response.content, state);
    },
    getSectorDetailFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getSectorDetails, CallStatus.ERROR, state, status);
    },
    getNonWorkingDaysAndMarketTime(state, action: PayloadAction<string>) {
      setFundamentalCallStatus(FundamentalCall.getNonWorkingDaysAndMarketTime, CallStatus.PENDING, state);
    },
    getNonWorkingDaysAndMarketTimeCompleted(state, action: PayloadAction<NonWorkingDaysResponse>) {
      setFundamentalCallStatus(FundamentalCall.getNonWorkingDaysAndMarketTime, CallStatus.READY, state);
    },
    getNonWorkingDaysAndMarketTimeFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getNonWorkingDaysAndMarketTime, CallStatus.ERROR, state, status);
    },
    getETFs(state, action: PayloadAction<ETFParams>) {
      setFundamentalCallStatus(FundamentalCall.getETFs, CallStatus.PENDING, state);
    },
    getETFsCompleted(state, action: PayloadAction<PagedResponse<ETF>>) {
      const { content, totalElements } = action.payload;
      state.ETFData.totalElements = totalElements;
      if (content.length > 1) {
        const etfs = content.map((etf: ETF) => {
          const { name, ...rest } = etf;
          return { companyName: name && parseETFName(name), ...rest };
        });
        state.ETFData.content = [ ...state.ETFData.content, ...etfs ];
        if (state.ETFData.content.length === state.ETFData.totalElements) {
          state.ETFData.nextPage = -1;
        } else {
          state.ETFData.nextPage += 1;
        }
      }
      setFundamentalCallStatus(FundamentalCall.getETFs, CallStatus.READY, state);
    },
    getETFsFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getETFs, CallStatus.ERROR, state, status);
    },
    getETFDetails(state, action: PayloadAction<ETFDetailParams>) {
      setFundamentalCallStatus(FundamentalCall.getETFDetails, CallStatus.PENDING, state);
    },
    getETFDetailsCompleted(state, action: PayloadAction<PagedResponse<ETFDetailResponse>>) {
      const details = action.payload.content;
      const updatedETFs = state.ETFData.content.map(etf1 => {
        const matchingEtf = details.find((etf2: ETFDetailResponse) => etf2.symbol === etf1.symbol);
        if (matchingEtf) {
          return { ...etf1, ...matchingEtf };
        }
        return etf1;
      });

      state.ETFData.content = [ ...updatedETFs ];
      setFundamentalCallStatus(FundamentalCall.getETFDetails, CallStatus.READY, state);
    },
    getETFDetailsFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getETFDetails, CallStatus.ERROR, state, status);
    },
    getStatements(state, action: PayloadAction<BaseStatementSearchParamType>) {
      state.statementsData.symbol = action.payload.symbol;
      setFundamentalCallStatus(FundamentalCall.getStatements, CallStatus.PENDING, state);
    },
    getStatementsCompleted(state, action: PayloadAction<StatementApiResponse>) {
      state.statementsData.balanceSheet = action.payload.balanceSheet;
      state.statementsData.cashFlow = action.payload.cashFlow;
      state.statementsData.income = action.payload.income;
      setFundamentalCallStatus(FundamentalCall.getStatements, CallStatus.READY, state);
    },
    getStatementsFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getStatements, CallStatus.ERROR, state, status);
    },
    getStatementsCashFlow(state, action: PayloadAction<BaseStatementSearchParamType>) {
      state.statementsData.symbol = action.payload.symbol;
      setFundamentalCallStatus(FundamentalCall.getStatementsCashFlow, CallStatus.PENDING, state);
    },
    getStatementsCashFlowCompleted(state, action: PayloadAction<CashFlow[]>) {
      state.statementsData.cashFlow = action.payload;
      setFundamentalCallStatus(FundamentalCall.getStatementsCashFlow, CallStatus.READY, state);
    },
    getStatementsCashFlowFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getStatementsCashFlow, CallStatus.ERROR, state, status);
    },
    getStatementsBalanceSheet(state, action: PayloadAction<BaseStatementSearchParamType>) {
      state.statementsData.symbol = action.payload.symbol;
      setFundamentalCallStatus(FundamentalCall.getStatementsBalanceSheet, CallStatus.PENDING, state);
    },
    getStatementsBalanceSheetCompleted(state, action: PayloadAction<BalanceSheet[]>) {
      state.statementsData.balanceSheet = action.payload;
      setFundamentalCallStatus(FundamentalCall.getStatementsBalanceSheet, CallStatus.READY, state);
    },
    getStatementsBalanceSheetFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getStatementsBalanceSheet, CallStatus.ERROR, state, status);
    },
    getStatementsIncome(state, action: PayloadAction<BaseStatementSearchParamType>) {
      state.statementsData.symbol = action.payload.symbol;
      setFundamentalCallStatus(FundamentalCall.getStatementsIncome, CallStatus.PENDING, state);
    },
    getStatementsIncomeCompleted(state, action: PayloadAction<Income[]>) {
      state.statementsData.income = action.payload;
      setFundamentalCallStatus(FundamentalCall.getStatementsIncome, CallStatus.READY, state);
    },
    getStatementsIncomeFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getStatementsIncome, CallStatus.ERROR, state, status);
    },
    getWeek52HighLow(state, action: PayloadAction<Week52HighLowParamType>) {
      const { changeStatus } = action.payload;
      state.week52Direction = changeStatus ?? Week52HighLowStatusType.Gainers;
      setFundamentalCallStatus(FundamentalCall.getWeek52HighLow, CallStatus.PENDING, state);
    },
    getWeek52HighLowCompleted(state, action: PayloadAction<SimplePagedResponse<Week52HighLowResponse>>) {
      const { content, size, totalPages, totalElements } = action.payload;
      const pageSize = 10;
      const target = state.week52Direction === Week52HighLowStatusType.Gainers
        ? state.week52High : state.week52Low;
      target.totalElements = totalElements;
      target.size = size;
      target.totalPages = totalPages;
      target.content = target.content.concat(content);
      target.numberOfElements = target.content.length;
      target.number = Math.floor(target.content.length / pageSize);
      setFundamentalCallStatus(FundamentalCall.getWeek52HighLow, CallStatus.READY, state);
    },
    getWeek52HighLowFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getWeek52HighLow, CallStatus.ERROR, state, status);
    },
    getWeek52HighLowTopList(state) {
      state.week52HighLowTopList.topNLow = [];
      state.week52HighLowTopList.topNHigh = [];
      setFundamentalCallStatus(FundamentalCall.getWeek52HighLowTopList, CallStatus.PENDING, state);
    },
    getWeek52HighLowTopListCompleted(state, action: PayloadAction<Week52HighLowTopListResponse>) {
      const { topNHigh, topNLow } = action.payload;
      state.week52HighLowTopList = { topNHigh, topNLow };
      updateCompanyAndLogoCacheWithName(topNLow, state);
      updateCompanyAndLogoCacheWithName(topNHigh, state);
      setFundamentalCallStatus(FundamentalCall.getWeek52HighLowTopList, CallStatus.READY, state);
    },
    getWeek52HighLowTopListFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getWeek52HighLowTopList, CallStatus.ERROR, state, status);
    },
    getAllRatios(state, action: PayloadAction<BaseRatiosParamType>) {
      state.ratiosData = DEFAULT_RATIOS_AND_FILTER_PARAMS;
      setFundamentalCallStatus(FundamentalCall.getAllRatios, CallStatus.PENDING, state);
    },
    getAllRatiosCompleted(state, action: PayloadAction<RatiosResponse>) {
      const { payload } = action;
      state.ratiosData = payload;
      setFundamentalCallStatus(FundamentalCall.getAllRatios, CallStatus.READY, state);
    },
    getAllRatiosFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getAllRatios, CallStatus.ERROR, state, status);
    },
    getRatiosValuation(state, action: PayloadAction<BaseRatiosParamType>) {
      state.ratiosData.valuation = DEFAULT_RATIOS_AND_FILTER_PARAMS.valuation;
      setFundamentalCallStatus(FundamentalCall.getRatiosValuation, CallStatus.PENDING, state);
    },
    getRatiosValuationCompleted(state, action: PayloadAction<ValuationRatiosResponse>) {
      const { payload } = action;
      state.ratiosData.valuation = payload;
      setFundamentalCallStatus(FundamentalCall.getRatiosValuation, CallStatus.READY, state);
    },
    getRatiosValuationFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getRatiosValuation, CallStatus.ERROR, state, status);
    },
    getRatiosProfitability(state, action: PayloadAction<BaseRatiosParamType>) {
      state.ratiosData.profitability = DEFAULT_RATIOS_AND_FILTER_PARAMS.profitability;
      setFundamentalCallStatus(FundamentalCall.getRatiosProfitability, CallStatus.PENDING, state);
    },
    getRatiosProfitabilityCompleted(state, action: PayloadAction<ProfitabilityRatiosResponse>) {
      const { payload } = action;
      state.ratiosData.profitability = payload;
      setFundamentalCallStatus(FundamentalCall.getRatiosProfitability, CallStatus.READY, state);
    },
    getRatiosProfitabilityFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getRatiosProfitability, CallStatus.ERROR, state, status);
    },
    getRatiosLiquidityAndSolvency(state, action: PayloadAction<BaseRatiosParamType>) {
      state.ratiosData.liquidityAndSolvency = DEFAULT_RATIOS_AND_FILTER_PARAMS.liquidityAndSolvency;
      setFundamentalCallStatus(FundamentalCall.getRatiosLiquidityAndSolvency, CallStatus.PENDING, state);
    },
    getRatiosLiquidityAndSolvencyCompleted(state, action: PayloadAction<LiquidityAndSolvencyRatiosResponse>) {
      const { payload } = action;
      state.ratiosData.liquidityAndSolvency = payload;
      setFundamentalCallStatus(FundamentalCall.getRatiosLiquidityAndSolvency, CallStatus.READY, state);
    },
    getRatiosLiquidityAndSolvencyFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getRatiosLiquidityAndSolvency, CallStatus.ERROR, state, status);
    },
    getGappers(state, action: PayloadAction<GapperParamType>) {
      const { page } = action.payload;
      if (page === 0) {
        state.gappersData = { ...DEFAULT_PAGE_RESPONSE };
      }
      state.gappersData.number = page ?? 0;
      setFundamentalCallStatus(FundamentalCall.getGappers, CallStatus.PENDING, state);
    },
    getGappersCompleted(state, action: PayloadAction<SimplePagedResponse<GapperResponse>>) {
      const { content, size, totalPages, totalElements } = action.payload;
      state.gappersData.totalElements = totalElements;
      state.gappersData.size = size;
      state.gappersData.totalPages = totalPages;
      state.gappersData.content = state.gappersData.content?.concat(...content);
      state.gappersData.numberOfElements = state.gappersData.content?.length;
      updateCompanyAndLogoCacheWithName(content, state);
      setFundamentalCallStatus(FundamentalCall.getGappers, CallStatus.READY, state);
    },
    getGappersFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getGappers, CallStatus.ERROR, state, status);
    },
    getAvailableIndexes(state, action: PayloadAction) {
      setFundamentalCallStatus(FundamentalCall.getAvailableIndexes, CallStatus.PENDING, state);
    },
    getAvailableIndexesCompleted(state, action: PayloadAction<AvailableIndexesResponse[]>) {
      const availableIndexes: AvailableIndexesResponse[] = [];

      // Using sorted market data indexes list
      marketIndexesArray.forEach(marketIndex => {
        const foundedPayload = action.payload.find(payloadItem => payloadItem.asYahoo === marketIndex.symbol);
        if (foundedPayload) {
          availableIndexes.push({ ...foundedPayload, name: marketIndex.shownAs });// This line is to rename them in FE
        }
      });

      state.availableIndexes = availableIndexes;
      setFundamentalCallStatus(FundamentalCall.getAvailableIndexes, CallStatus.READY, state);
    },
    getAvailableIndexesFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getAvailableIndexes, CallStatus.ERROR, state, status);
    },
    getMarketIndexes(state, action: PayloadAction) {
      setFundamentalCallStatus(FundamentalCall.getMarketIndexes, CallStatus.PENDING, state);
    },
    getMarketIndexesCompleted(state, action: PayloadAction<[]>) {
      setFundamentalCallStatus(FundamentalCall.getMarketIndexes, CallStatus.READY, state);
      state.marketIndexes = action.payload;
    },
    getMarketIndexesFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getMarketIndexes, CallStatus.ERROR, state, status);
    },
    getMarketCaps(state, action: PayloadAction<MarketCapsParamType>) {
      const { category, page } = action.payload;
      const isFirstPage = (page ?? DEFAULT_PAGE) === DEFAULT_PAGE;
      const hasCategoryChanged = state.marketCapsData.params.category !== category;

      // Only a fresh bucket wipes the lists - paging keeps the rows already on screen.
      if (isFirstPage || hasCategoryChanged) {
        state.marketCapsData.responseData = DEFAULT_MARKET_CAPS_DATA.responseData;
      }

      state.marketCapsData.params = action.payload;
      setFundamentalCallStatus(FundamentalCall.getMarketCaps, CallStatus.PENDING, state);
    },
    getMarketCapsCompleted(state, action: PayloadAction<MarketCapsSplitData>) {
      const { gainers, losers } = action.payload;

      state.marketCapsData.responseData = {
        gainers: mergeMarketCapsPage(state.marketCapsData.responseData.gainers, gainers),
        losers: mergeMarketCapsPage(state.marketCapsData.responseData.losers, losers),
      };
      setFundamentalCallStatus(FundamentalCall.getMarketCaps, CallStatus.READY, state);
    },
    getMarketCapsFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      state.marketCapsData.responseData = DEFAULT_MARKET_CAPS_DATA.responseData;
      setFundamentalCallStatus(FundamentalCall.getMarketCaps, CallStatus.ERROR, state, status);
    },
    getEpicMovers(state, action: PayloadAction<EpicMoversParamType>) {
      state.epicMoversData.responseData = DEFAULT_EPIC_MOVERS_DATA.responseData;
      state.epicMoversData.params = action.payload;
      setFundamentalCallStatus(FundamentalCall.getEpicMovers, CallStatus.PENDING, state);
    },
    getEpicMoversCompleted(state, action: PayloadAction<EpicMoversResponse>) {
      const { payload } = action;
      state.epicMoversData.responseData = payload;
      updateCompanyAndLogoCacheWithName(payload.topGainers, state);
      updateCompanyAndLogoCacheWithName(payload.topLosers, state);
      setFundamentalCallStatus(FundamentalCall.getEpicMovers, CallStatus.READY, state);
    },
    getEpicMoversFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setFundamentalCallStatus(FundamentalCall.getEpicMovers, CallStatus.ERROR, state, status);
    },
  },
});

export const {
  clearStockSearch,
  searchStockByParams,
  searchStock,
  stockSearchFailed,
  stockSearchCompleted,
  stockSearchReset,
  loadStock,
  searchEarnings,
  searchEarningsCompleted,
  setEarningsCalendarSearchState,
  getPreviousEarnings,
  getPreviousEarningsCompleted,
  getPreviousEarningsFailed,
  searchEarningsFailed,
  keyStatsFailed,
  keyStatsCompleted,
  getCompany,
  getCompanyCompleted,
  getCompanyFailed,
  getCompanyLogoAndName,
  getCompanyLogoAndNameCompleted,
  getCompanyLogoAndNameFailed,
  getPopularStocks,
  getPopularStocksCompleted,
  getPopularStocksFailed,
  getNewStocks,
  getNewStocksCompleted,
  getNewStocksFailed,
  getDividendPayers,
  getDividendPayersCompleted,
  getDividendPayersFailed,
  getHighGrowthCompanies,
  getHighGrowthCompaniesCompleted,
  getHighGrowthCompaniesFailed,
  getSectors,
  getSectorsCompleted,
  getSectorsFailed,
  getSectorDetail,
  getSectorDetailCompleted,
  getSectorDetailFailed,
  getNonWorkingDaysAndMarketTime,
  getNonWorkingDaysAndMarketTimeCompleted,
  getNonWorkingDaysAndMarketTimeFailed,
  getETFs,
  getETFsCompleted,
  getETFsFailed,
  getETFDetails,
  getETFDetailsCompleted,
  getETFDetailsFailed,
  getStatements,
  getStatementsCompleted,
  getStatementsFailed,
  getStatementsCashFlow,
  getStatementsCashFlowCompleted,
  getStatementsCashFlowFailed,
  getStatementsBalanceSheet,
  getStatementsBalanceSheetCompleted,
  getStatementsBalanceSheetFailed,
  getStatementsIncome,
  getStatementsIncomeCompleted,
  getStatementsIncomeFailed,
  getWeek52HighLow,
  getWeek52HighLowCompleted,
  getWeek52HighLowFailed,
  getWeek52HighLowTopList,
  getWeek52HighLowTopListCompleted,
  getWeek52HighLowTopListFailed,
  getAllRatios,
  getAllRatiosCompleted,
  getAllRatiosFailed,
  getRatiosValuation,
  getRatiosValuationCompleted,
  getRatiosValuationFailed,
  getEpicMovers,
  getEpicMoversCompleted,
  getEpicMoversFailed,
  getMarketCaps,
  getMarketCapsCompleted,
  getMarketCapsFailed,
  getRatiosProfitability,
  getRatiosProfitabilityCompleted,
  getRatiosProfitabilityFailed,
  getRatiosLiquidityAndSolvency,
  getRatiosLiquidityAndSolvencyCompleted,
  getRatiosLiquidityAndSolvencyFailed,
  getGappers,
  getGappersCompleted,
  getGappersFailed,
  getAvailableIndexes,
  getAvailableIndexesCompleted,
  getAvailableIndexesFailed,
  getMarketIndexes,
  getMarketIndexesCompleted,
  getMarketIndexesFailed,
} = fundamentalSlice.actions;

export default fundamentalSlice.reducer;
